import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

interface Props {
  data: number[][];                    // [day 0..6][hour 0..23]
  title?: string;
  subtitle?: string;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function PunchCard({
  data,
  title = 'Activity Punch-card',
  subtitle = 'When this user actually does the work — last 90 days'
}: Props) {
  const max = Math.max(1, ...data.flat());

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-500/10">
            <Clock className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid grid-cols-[40px_1fr] gap-2">
            <div />
            <div className="grid grid-cols-24" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
              {Array.from({ length: 24 }).map((_, h) => (
                <div
                  key={h}
                  className={`text-center text-[8px] font-semibold uppercase tracking-wide ${
                    h % 4 === 0 ? 'text-slate-500 dark:text-slate-400' : 'text-transparent'
                  }`}
                >
                  {h}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-1.5 space-y-1">
            {DAY_LABELS.map((day, dIdx) => (
              <div key={day} className="grid grid-cols-[40px_1fr] items-center gap-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{day}</div>
                <div className="grid gap-[2px]" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                  {(data[dIdx] ?? Array(24).fill(0)).map((value, h) => {
                    const pct = value / max;
                    const size = pct === 0 ? 6 : 8 + Math.round(pct * 14);
                    return (
                      <div
                        key={h}
                        className="relative flex h-6 items-center justify-center"
                        title={`${day} ${h}:00 — ${value} actions`}
                      >
                        <div
                          className={`rounded-full transition ${
                            pct === 0
                              ? 'bg-slate-200 dark:bg-slate-700'
                              : pct < 0.34
                                ? 'bg-indigo-300'
                                : pct < 0.67
                                  ? 'bg-indigo-500'
                                  : 'bg-indigo-700'
                          }`}
                          style={{ width: size, height: size }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-300" /> Idle</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-indigo-300" /> Light</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-indigo-500" /> Active</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-indigo-700" /> Heavy</span>
      </div>
    </motion.div>
  );
}
